import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Globe } from 'lucide-react';
import Logo from './Logo';
import { COMPANY } from '@/lib/data';
import { openConsentSettings } from '@/lib/consent';

const FOOTER_LINKS = [
  {
    title: 'Tjenester',
    links: [
      { label: 'Privat', to: '/tjenester/privat' },
      { label: 'Bedrift & borettslag', to: '/tjenester/bedrift-borettslag' },
      { label: 'Rehab & riving', to: '/tjenester/rehab-riving' },
      { label: 'Storsekker', to: '/tjenester/storsekker' },
    ],
  },
  {
    title: 'Info',
    links: [
      { label: 'Priser', to: '/priser' },
      { label: 'Fraksjoner', to: '/fraksjoner' },
      { label: 'Utstyr', to: '/utstyr' },
      { label: 'Nyheter', to: '/nyheter' },
    ],
  },
  {
    title: 'Om oss',
    links: [
      { label: 'Om BMG', to: '/om-oss' },
      { label: 'Referanser', to: '/kunder' },
      { label: 'Miljø', to: '/miljo' },
      { label: 'Kontakt', to: '/kontakt' },
      { label: 'Vilkår', to: '/vilkar' },
      { label: 'Personvern', to: '/personvern' },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="bg-bmg-ink text-bmg-white mt-auto">
      <div className="container-bmg py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company info */}
          <div className="lg:col-span-2">
            <div className="bg-bmg-white inline-block rounded-lg px-3 py-2 mb-4">
              <Logo />
            </div>
            <p className="text-neutral-300 text-sm leading-relaxed max-w-xs">
              Lokalt eid og drevet avfallsselskap i Bergen. Containerutleie, riving og
              avfallshåndtering for privat og bedrift. Gaselle-bedrift 2024.
            </p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-bmg-yellow flex-shrink-0 mt-0.5" />
                <a href={COMPANY.mapsUrl} target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-bmg-yellow transition-colors">
                  {COMPANY.address}, {COMPANY.postalCode}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-bmg-yellow" />
                <a href={`tel:${COMPANY.phoneRaw}`} className="text-neutral-300 hover:text-bmg-yellow transition-colors">
                  {COMPANY.phone}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-bmg-yellow" />
                <a href={`mailto:${COMPANY.email}`} className="text-neutral-300 hover:text-bmg-yellow transition-colors">
                  {COMPANY.email}
                </a>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-bmg-yellow flex-shrink-0 mt-0.5" />
                <span className="text-neutral-300">{COMPANY.hours}</span>
              </div>
            </div>
          </div>

          {/* Link columns */}
          {FOOTER_LINKS.map((col) => (
            <div key={col.title}>
              <h2 className="text-sm font-bold uppercase tracking-wide text-bmg-yellow mb-3">
                {col.title}
              </h2>
              <ul className="space-y-2">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-neutral-300 hover:text-bmg-yellow transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-neutral-700 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-neutral-400">
            &copy; {new Date().getFullYear()} {COMPANY.legalName}. Alle rettigheter forbeholdt.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <a
              href={COMPANY.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-300 hover:text-bmg-yellow transition-colors flex items-center gap-1.5 text-sm"
            >
              <Globe className="h-4 w-4" />
              Facebook
            </a>
            <Link to="/vilkar" className="text-neutral-300 hover:text-bmg-yellow transition-colors text-sm">
              Vilkår
            </Link>
            <Link to="/personvern" className="text-neutral-300 hover:text-bmg-yellow transition-colors text-sm">
              Personvern
            </Link>
            <button
              type="button"
              onClick={openConsentSettings}
              className="text-neutral-300 hover:text-bmg-yellow transition-colors text-sm"
            >
              Informasjonskapsler
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
